/**
 * These patterns are considered "clearable" in that they are to be considered temporary
 * to the UI experience. Unlike patterns like :runtime which are essential for the UI
 * to function and should only be cleared on logout - items with these patterns accumulate
 * over time, and can be comfortably cleared or purged at any time to make room for new keys
 */
const CLEARABLE_PATTERNS = [
  /^.*-table-resizeColumnWidths$/,
  /^.*:columns$/,
  /^.*-table-selectedCellRange$/,
]

const STORAGE_QUEUE_KEY = ':runtime-queue'

const temporaryStorageData: { [key: string]: string | undefined } = {}

const temporaryStorage = {
  getItem(key: string): string | undefined {
    if (key in temporaryStorageData) {
      return temporaryStorageData[key]
    }
    return undefined
  },
  removeItem(key: string) {
    delete temporaryStorageData[key]
  },
  setItem(key: string, value: string | undefined) {
    temporaryStorageData[key] = value
  },
}

/* c8 ignore start */
const getStorageEngine = () => {
  try {
    return typeof localStorage === 'object' ? localStorage : temporaryStorage
  } catch (error) {
    console.error('Cannot access localStorage')
    //console.error(error)
  }
  return temporaryStorage
}
/* c8 ignore stop */

let storageEngine = getStorageEngine()

export const storage = <TData extends Record<string, unknown>>(key: string) => {
  let clearAttempts = 0

  /**
   * Attempts to clear space in localStorage - However, if sufficient storage cannot be cleared
   * after clearing a certain portion of the queue, then the application error should be thrown
   * to eliminate the risk of an infinite loop
   */
  const clearWithRetry = (dataToStore: any, setMethod: (data: any) => void) => {
    if (clearAttempts >= 10) {
      throw new Error(
        'Storage is too full, and maximum clear attempts exceeded.'
      )
    }
    const itemCleared = clearStaleStorage()
    if (!itemCleared) {
      throw new Error('No clearable items available to free up storage.')
    }
    clearAttempts++
    setMethod(dataToStore)
  }

  return {
    clear() {
      storageEngine.removeItem(key)
    },

    exists(): boolean {
      const data = storageEngine.getItem(key)
      return typeof data === 'string' && data.length > 0
    },

    getParsed() {
      try {
        const raw = this.getRaw()
        return raw ? (JSON.parse(raw) as TData) : ({} as TData)
      } catch (e: any) {
        throw new Error(e)
      }
    },

    getRaw(): string | undefined {
      const item = storageEngine.getItem(key)
      return item === null ? undefined : item
    },

    set(data: any | undefined) {
      let dataToStore = data

      try {
        if (dataToStore === undefined) {
          return this.clear()
        }

        if (typeof dataToStore !== 'string') {
          dataToStore = JSON.stringify(dataToStore)
        }

        storageEngine.setItem(key, dataToStore)

        updateRuntimeQueue(key)
      } catch (error: any) {
        if (error.name === 'QuotaExceededError') {
          clearWithRetry(dataToStore, this.set.bind(this))
        } else {
          throw error
        }
      }
    },

    setRaw(data?: string) {
      try {
        if (data === undefined) {
          return this.clear()
        }
        storageEngine.setItem(key, data)
        updateRuntimeQueue(key)
      } catch (error: any) {
        if (error.name === 'QuotaExceededError') {
          clearWithRetry(data, this.setRaw.bind(this))
        } else {
          throw error
        }
      }
    },

    useTemporaryStorage() {
      storageEngine = temporaryStorage
    },
  }
}

/**
 * Simple utility for fetching the runtime queue of keys used for determining the order
 * by which keys should be removed (items at the end of the queue are FRESH)
 */
const getRuntimeQueue = () => {
  const rawQueue = storageEngine.getItem(STORAGE_QUEUE_KEY) ?? '[]'
  const storedQueue = JSON.parse(rawQueue)
  return new Set<string>(storedQueue)
}

/**
 * Updates the runtime queue of keys by ADDING new items, or MOVING existing items to the
 * end of the queue since items will be cleared from the queue starting from the FRONT
 */
const updateRuntimeQueue = (key: string) => {
  if (key && isClearablePattern(key)) {
    const currentRuntimeQueue = getRuntimeQueue()

    // If the item is in the queue, it is deleted so it can be re-added to the end of the queue
    if (currentRuntimeQueue.has(key)) {
      currentRuntimeQueue.delete(key)
    }
    currentRuntimeQueue.add(key)

    storageEngine.setItem(
      STORAGE_QUEUE_KEY,
      JSON.stringify(Array.from(currentRuntimeQueue))
    )
  }
}

/**
 * All items in localStorage are not clearable (i.e. :runtime which tracks Auth state)
 * Therefore, before potentially clearing an item from storage, we must make sure that
 * the item is clearable, by checking its pattern against our set of clearable keys
 */
const isClearablePattern = (key: string): boolean => {
  return CLEARABLE_PATTERNS.some((pattern) => pattern.test(key))
}

/**
 * Gets the runtime-queue and using the first item key, removes
 * the item from localStorage to make room for newer data
 */
const clearStaleStorage = () => {
  const currentRuntimeQueue = getRuntimeQueue()

  const firstKey = currentRuntimeQueue.values().next().value

  if (firstKey) {
    storageEngine.removeItem(firstKey)
    currentRuntimeQueue.delete(firstKey)

    storageEngine.setItem(
      STORAGE_QUEUE_KEY,
      JSON.stringify(Array.from(currentRuntimeQueue))
    )
    return true
  }
  return false
}
